import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box, Container, Typography, Button, Stack, Grid, Chip, Link as MuiLink,
} from '@mui/material';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import LayersRoundedIcon from '@mui/icons-material/LayersRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import AuroraBackground from './components/AuroraBackground';
import BrandLogo from './components/BrandLogo';
import ProductPreview from './components/ProductPreview';
import WaitlistModal from './components/WaitlistModal';
import { getAppUrl } from '../../utils/deploymentMode';
import { docsUrl } from '../../config/websiteCaptcha';
import { getSignupConfig } from '../../services/stripeApi';

const SIGNUP_URL = '/signup';

/* ── Brand tokens (matches the app: blue → green "V", GitHub-dark surfaces) ── */
const INK = '#e6edf3';
const MUTED = '#9aa6b8';
const BLUE = '#3b82f6';
const GREEN = '#22c55e';
const GRAD = 'linear-gradient(135deg,#2563eb 0%,#10b981 100%)';
const TEXT_GRAD = { background: 'linear-gradient(120deg,#ffffff 0%,#bcd2ff 42%,#86efac 100%)',
  WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' };
const DOCS = docsUrl();

/* Reveal-on-scroll wrapper (reduced-motion aware). */
const Reveal = ({ children, delay = 0, sx }) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setShown(true); return; }
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } },
      { threshold: 0.15 });
    io.observe(el); return () => io.disconnect();
  }, []);
  return (
    <Box ref={ref} sx={{ opacity: shown ? 1 : 0, transform: shown ? 'none' : 'translateY(22px)',
      transition: `opacity .7s ease ${delay}ms, transform .7s cubic-bezier(.2,.8,.2,1) ${delay}ms`, ...sx }}>
      {children}
    </Box>
  );
};

/* Drives the scroll-reactive background: reports its theme when scrolled to center. */
const SceneSection = ({ theme, onActive, children, ...rest }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) onActive(theme); },
      { rootMargin: '-40% 0px -40% 0px', threshold: 0 });
    io.observe(el); return () => io.disconnect();
  }, [theme, onActive]);
  return <Box ref={ref} {...rest}>{children}</Box>;
};

const PILLARS = [
  { icon: <TravelExploreRoundedIcon />, title: 'Synthetic Monitoring',
    blurb: 'Scripted browser and API checks from cloud and on-prem agents — test the real journey, even on private networks.' },
  { icon: <AccountTreeRoundedIcon />, title: 'APM & Distributed Tracing',
    blurb: 'Auto-instrumented services, a live service map, and method-level profiling to find root cause fast.' },
  { icon: <SpeedRoundedIcon />, title: 'Real User Monitoring',
    blurb: 'Core Web Vitals, sessions, errors and funnels from your actual visitors, on real devices.' },
  { icon: <AutoFixHighRoundedIcon />, title: 'Workbooks',
    blurb: 'Programmable runbooks across UI, API, traces, DB, logs and network — that explain themselves and can act.' },
  { icon: <AutoAwesomeRoundedIcon />, title: 'AI Quality & Sentinel',
    blurb: 'Evaluate-not-assert monitoring for chatbots and agents: correctness, faithfulness, safety — scored by an LLM judge.' },
  { icon: <NotificationsActiveRoundedIcon />, title: 'Alerting, On-call & SLOs',
    blurb: 'Rules, severities, on-call schedules, silences and error-budget burn alerts — page the right person first.' },
  { icon: <HubRoundedIcon />, title: 'Network & Infrastructure',
    blurb: 'Path health, hosts, containers and Kubernetes — with agents that reach where SaaS probes cannot.' },
  { icon: <ForumRoundedIcon />, title: 'Incident Response',
    blurb: 'War Rooms that auto-assemble topology, signals and a timeline — so you start with the picture, not a blank page.' },
];

const VALUES = [
  { icon: <LayersRoundedIcon />, title: 'One correlated story',
    text: 'Synthetic, traces, RUM, logs and infra share context — so root cause is one click, not a 3 a.m. scavenger hunt.' },
  { icon: <VisibilityRoundedIcon />, title: 'Reaches private infra, and acts',
    text: 'On-prem agents monitor databases and internal apps; Workbooks remediate, gate deploys, and page people.' },
  { icon: <AutoAwesomeRoundedIcon />, title: 'AI-native by design',
    text: 'Agentic narration explains incidents, a Discovery Agent auto-authors monitors, and you can monitor AI itself.' },
];

const NavLink = ({ href, children, external }) => (
  <MuiLink href={href} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    sx={{ color: MUTED, fontSize: 14.5, fontWeight: 500, textDecoration: 'none', display: 'inline-flex',
      alignItems: 'center', gap: 0.5, transition: 'color .2s', '&:hover': { color: INK } }}>
    {children}{external && <OpenInNewRoundedIcon sx={{ fontSize: 15 }} />}
  </MuiLink>
);

const PrimaryButton = ({ children, onClick, href, sx, ...rest }) => (
  <Button onClick={onClick} href={href} {...rest}
    sx={{ px: 3, py: 1.2, borderRadius: 2, textTransform: 'none', fontWeight: 700, fontSize: 15.5,
      color: '#fff', background: GRAD, boxShadow: '0 8px 26px rgba(37,99,235,0.32)',
      transition: 'transform .2s, box-shadow .2s, filter .2s', '&:hover': { transform: 'translateY(-2px)',
        boxShadow: '0 14px 38px rgba(16,185,129,0.36)', filter: 'brightness(1.06)' }, ...sx }}>
    {children}
  </Button>
);

const GhostButton = ({ children, ...rest }) => (
  <Button {...rest} sx={{ px: 3, py: 1.2, borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: 15.5,
    color: INK, border: '1px solid rgba(255,255,255,0.16)', background: 'rgba(255,255,255,0.03)',
    backdropFilter: 'blur(6px)', '&:hover': { borderColor: 'rgba(59,130,246,0.6)', background: 'rgba(59,130,246,0.10)' } }}>
    {children}
  </Button>
);

const GlassCard = ({ children, sx, hover = true }) => (
  <Box sx={{ p: 3, height: '100%', borderRadius: 3, background: 'rgba(22,27,34,0.55)',
    border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(12px)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)', transition: 'transform .25s, border-color .25s, box-shadow .25s',
    ...(hover && { '&:hover': { transform: 'translateY(-5px)', borderColor: 'rgba(59,130,246,0.42)',
      boxShadow: '0 22px 50px rgba(0,0,0,0.4)' } }), ...sx }}>
    {children}
  </Box>
);

const IconBadge = ({ children }) => (
  <Box sx={{ width: 46, height: 46, borderRadius: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'center',
    mb: 1.5, color: '#cfe0ff', background: 'linear-gradient(135deg,rgba(37,99,235,0.28),rgba(16,185,129,0.20))',
    border: '1px solid rgba(255,255,255,0.1)', '& svg': { fontSize: 24 } }}>{children}</Box>
);

const LandingPage = () => {
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState('signal');
  const [progress, setProgress] = useState(0);
  const [signupEnabled, setSignupEnabled] = useState(false);
  const join = () => setOpen(true);
  const onActive = useCallback((t) => setTheme(t), []);

  // When the operator has turned on public signup (master switch + at least one
  // published plan), the marketing CTAs become "Start free trial" → /signup and
  // the waitlist is retired. Otherwise we keep the waitlist.
  useEffect(() => {
    getSignupConfig()
      .then((cfg) => setSignupEnabled(!!(cfg && cfg.enabled && Array.isArray(cfg.plans) && cfg.plans.length > 0)))
      .catch(() => setSignupEnabled(false));
  }, []);

  // Primary CTA target: route to signup when enabled, else open the waitlist modal.
  const ctaProps = signupEnabled ? { href: SIGNUP_URL } : { onClick: join };
  const ctaLabel = signupEnabled ? 'Signup' : 'Join the waitlist';
  const ctaShort = signupEnabled ? 'Sign up' : 'Join';

  // Top scroll-progress bar.
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      setProgress(max > 0 ? (h.scrollTop / max) * 100 : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <Box sx={{ position: 'relative', minHeight: '100dvh', bgcolor: '#0a0d12', color: INK, overflowX: 'hidden',
      fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif' }}>
      <AuroraBackground theme={theme} />

      {/* scroll progress */}
      <Box sx={{ position: 'fixed', top: 0, left: 0, height: 3, width: `${progress}%`, zIndex: 40,
        background: GRAD, boxShadow: '0 0 10px rgba(16,185,129,0.5)', transition: 'width .1s linear' }} />

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {/* ── NAV ── */}
        <Box component="header" sx={{ position: 'sticky', top: 0, zIndex: 20,
          backdropFilter: 'blur(12px)', background: 'rgba(10,13,18,0.6)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <Container maxWidth="lg">
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ height: 68 }}>
              <BrandLogo size={30} />
              <Stack direction="row" spacing={3.5} alignItems="center" sx={{ display: { xs: 'none', md: 'flex' } }}>
                <NavLink href="#features">Product</NavLink>
                <NavLink href="#why">Why VerOps</NavLink>
                <NavLink href={DOCS} external>Documentation</NavLink>
                <NavLink href={getAppUrl('/')}>Sign in</NavLink>
                <PrimaryButton {...ctaProps} sx={{ py: 0.9, px: 2.4, fontSize: 14.5 }}>{ctaLabel}</PrimaryButton>
              </Stack>
              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <PrimaryButton {...ctaProps} sx={{ py: 0.8, px: 2, fontSize: 14 }}>{ctaShort}</PrimaryButton>
              </Box>
            </Stack>
          </Container>
        </Box>

        {/* ── HERO ── */}
        <SceneSection theme="signal" onActive={onActive}>
          <Container maxWidth="lg" sx={{ pt: { xs: 7, md: 12 }, pb: { xs: 8, md: 12 }, textAlign: 'center' }}>
            <Reveal>
              <Stack alignItems="center" sx={{ mb: 3 }}>
                <BrandLogo size={84} hero wordmark={false} />
              </Stack>
              <Chip label="Unified observability + reliability" sx={{ mb: 3, px: 1, color: '#bcd2ff', fontWeight: 600,
                background: 'rgba(37,99,235,0.12)', border: '1px solid rgba(37,99,235,0.32)' }} />
              <Typography component="h1" sx={{ fontWeight: 850, lineHeight: 1.05, letterSpacing: '-0.025em',
                fontSize: { xs: 40, sm: 58, md: 76 }, mb: 2.5 }}>
                <Box component="span" sx={TEXT_GRAD}>See every layer.</Box><br />
                <Box component="span" sx={{ color: INK }}>Fix what actually matters.</Box>
              </Typography>
              <Typography sx={{ color: MUTED, fontSize: { xs: 17, md: 20 }, maxWidth: 760, mx: 'auto', lineHeight: 1.6 }}>
                VerOps connects synthetic monitoring, APM, RUM, programmable <strong style={{ color: INK }}>Workbooks</strong>,
                and first-class <strong style={{ color: INK }}>AI-quality</strong> monitoring into one platform — so you can
                answer the only question that matters: <em>is the whole journey healthy, and if not, why?</em>
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mt: 4.5 }}>
                <PrimaryButton {...ctaProps} endIcon={<ArrowForwardRoundedIcon />}>{ctaLabel}</PrimaryButton>
                <GhostButton href={DOCS} target="_blank" rel="noopener noreferrer" endIcon={<OpenInNewRoundedIcon />}>
                  Read the docs
                </GhostButton>
              </Stack>
              <Stack direction="row" spacing={3} justifyContent="center" flexWrap="wrap" useFlexGap
                sx={{ mt: 3, color: MUTED, fontSize: 13.5 }}>
                {['Cloud & on-prem agents', 'OpenTelemetry-native', 'Early-access waves'].map((t) => (
                  <Stack key={t} direction="row" spacing={0.6} alignItems="center">
                    <CheckCircleRoundedIcon sx={{ fontSize: 16, color: GREEN }} /><span>{t}</span>
                  </Stack>
                ))}
              </Stack>
            </Reveal>

            {/* realistic product preview (mirrors the actual VerOps app) */}
            <Reveal delay={150} sx={{ mt: { xs: 7, md: 10 } }}>
              <ProductPreview />
            </Reveal>
          </Container>
        </SceneSection>

        {/* ── TRUST STRIP ── */}
        <Container maxWidth="lg" sx={{ pb: { xs: 4, md: 8 } }}>
          <Typography sx={{ textAlign: 'center', color: '#6b7689', fontSize: 12.5, letterSpacing: 1.5, mb: 2 }}>
            ONE PLATFORM — EVERY SIGNAL ACROSS YOUR STACK
          </Typography>
          <Stack direction="row" spacing={{ xs: 2.5, md: 4.5 }} justifyContent="center" flexWrap="wrap" useFlexGap
            sx={{ opacity: 0.78, color: MUTED, fontWeight: 600, fontSize: { xs: 13, md: 14.5 } }}>
            {['OpenTelemetry', 'Linux & Windows hosts', 'Containers & Kubernetes', 'Browser & API checks',
              'Network path', 'Databases', 'LLM / AI endpoints'].map((t) => (
              <span key={t}>{t}</span>
            ))}
          </Stack>
        </Container>

        {/* ── PILLARS ── */}
        <SceneSection theme="topology" onActive={onActive}>
          <Container id="features" maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
            <Reveal>
              <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Typography sx={{ color: BLUE, fontWeight: 700, letterSpacing: 1.5, fontSize: 13, mb: 1 }}>
                  ONE PLATFORM, EVERY LAYER
                </Typography>
                <Typography component="h2" sx={{ fontWeight: 820, fontSize: { xs: 30, md: 44 }, letterSpacing: '-0.02em' }}>
                  Everything you need to keep it healthy
                </Typography>
              </Box>
            </Reveal>
            <Grid container spacing={2.5}>
              {PILLARS.map((p, i) => (
                <Grid item xs={12} sm={6} md={3} key={p.title}>
                  <Reveal delay={(i % 4) * 80}>
                    <GlassCard>
                      <IconBadge>{p.icon}</IconBadge>
                      <Typography sx={{ fontWeight: 700, fontSize: 17, mb: .8 }}>{p.title}</Typography>
                      <Typography sx={{ color: MUTED, fontSize: 14, lineHeight: 1.55 }}>{p.blurb}</Typography>
                    </GlassCard>
                  </Reveal>
                </Grid>
              ))}
            </Grid>
          </Container>
        </SceneSection>

        {/* ── WHY ── */}
        <SceneSection theme="trace" onActive={onActive}>
          <Container id="why" maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
            <Grid container spacing={3} alignItems="stretch">
              {VALUES.map((v, i) => (
                <Grid item xs={12} md={4} key={v.title}>
                  <Reveal delay={i * 100}>
                    <GlassCard sx={{ background: 'rgba(37,99,235,0.07)' }}>
                      <IconBadge>{v.icon}</IconBadge>
                      <Typography sx={{ fontWeight: 700, fontSize: 19, mb: 1 }}>{v.title}</Typography>
                      <Typography sx={{ color: MUTED, fontSize: 15, lineHeight: 1.6 }}>{v.text}</Typography>
                    </GlassCard>
                  </Reveal>
                </Grid>
              ))}
            </Grid>
          </Container>
        </SceneSection>

        {/* ── AI BAND ── */}
        <SceneSection theme="ai" onActive={onActive}>
          <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
            <Reveal>
              <GlassCard hover={false} sx={{ p: { xs: 4, md: 6 }, textAlign: 'center',
                background: 'radial-gradient(120% 140% at 50% 0%, rgba(16,185,129,0.16), rgba(22,27,34,0.6))',
                border: '1px solid rgba(16,185,129,0.25)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <Box component="img" src={`${process.env.PUBLIC_URL}/assets/vera.png`} alt="Vera"
                    sx={{ width: { xs: 64, md: 80 }, height: 'auto',
                      filter: 'drop-shadow(0 6px 18px rgba(16,185,129,0.45))' }} />
                </Box>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mb: 2.5, px: 1.6, py: 0.6,
                  borderRadius: 5, color: '#a7f3d0', fontWeight: 600, fontSize: 13.5,
                  background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)' }}>
                  Meet Vera and Sentinel
                </Box>
                <Typography component="h2" sx={{ fontWeight: 820, fontSize: { xs: 26, md: 38 }, mb: 2, letterSpacing: '-0.02em' }}>
                  A hallucination still returns <Box component="span" sx={{ color: GREEN }}>200 OK</Box>.
                </Typography>
                <Typography sx={{ color: MUTED, fontSize: { xs: 16, md: 18 }, maxWidth: 740, mx: 'auto', lineHeight: 1.65 }}>
                  AI features fail <em>semantically</em>, so VerOps gives them two AI-native answers.
                  <strong style={{ color: INK }}> Vera</strong> is your assistant — ask it about any incident or signal in
                  plain language and it narrates root cause, correlates telemetry and even drafts the monitors and
                  Workbooks to watch it. <strong style={{ color: INK }}>Sentinel</strong> is the AI-quality control plane —
                  an LLM-as-judge plus deterministic checks that continuously score correctness, faithfulness and safety,
                  gate releases in CI, and watch your AI in production.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mt: 3.5 }}>
                  <PrimaryButton {...ctaProps}>{signupEnabled ? ctaLabel : 'Get early access'}</PrimaryButton>
                  <GhostButton href={DOCS} target="_blank" rel="noopener noreferrer" endIcon={<OpenInNewRoundedIcon />}>
                    Explore the docs
                  </GhostButton>
                </Stack>
              </GlassCard>
            </Reveal>
          </Container>
        </SceneSection>

        {/* ── FINAL CTA ── */}
        <SceneSection theme="launch" onActive={onActive}>
          <Container maxWidth="md" sx={{ py: { xs: 8, md: 12 }, textAlign: 'center' }}>
            <Reveal>
              <BoltRoundedIcon sx={{ fontSize: 44, color: GREEN, mb: 1.5 }} />
              <Typography component="h2" sx={{ fontWeight: 850, fontSize: { xs: 32, md: 50 }, letterSpacing: '-0.025em', mb: 2 }}>
                <Box component="span" sx={TEXT_GRAD}>{signupEnabled ? 'Start monitoring today.' : 'Be first in line.'}</Box>
              </Typography>
              <Typography sx={{ color: MUTED, fontSize: 18, mb: 4, maxWidth: 560, mx: 'auto' }}>
                {signupEnabled
                  ? 'Spin up your workspace in minutes with a 14-day free trial — no credit card required.'
                  : "VerOps is rolling out in waves. Join the waitlist and we'll reach out the moment a slot opens."}
              </Typography>
              <PrimaryButton {...ctaProps} endIcon={<ArrowForwardRoundedIcon />}
                sx={{ px: 4, py: 1.5, fontSize: 17 }}>{ctaLabel}</PrimaryButton>
            </Reveal>
          </Container>
        </SceneSection>

        {/* ── FOOTER ── */}
        <Box component="footer" sx={{ borderTop: '1px solid rgba(255,255,255,0.07)', mt: 4 }}>
          <Container maxWidth="lg" sx={{ py: 5 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
              <BrandLogo size={26} />
              <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap" useFlexGap>
                <NavLink href={DOCS} external>Documentation</NavLink>
                <NavLink href="/terms" external>Terms of Service</NavLink>
                <NavLink href={getAppUrl('/')}>Sign in</NavLink>
                {signupEnabled ? (
                  <NavLink href={SIGNUP_URL}>{ctaLabel}</NavLink>
                ) : (
                  <MuiLink component="button" onClick={join} sx={{ color: MUTED, fontSize: 14.5, fontWeight: 500,
                    textDecoration: 'none', background: 'none', border: 0, cursor: 'pointer',
                    '&:hover': { color: INK } }}>Join the waitlist</MuiLink>
                )}
              </Stack>
              <Typography sx={{ color: '#5b6573', fontSize: 13 }}>
                © {new Date().getFullYear()} VerOps. All rights reserved.
              </Typography>
            </Stack>
          </Container>
        </Box>
      </Box>

      {!signupEnabled && <WaitlistModal open={open} onClose={() => setOpen(false)} />}
    </Box>
  );
};

export default LandingPage;
